import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type {
  NotificationOptimization,
  NotificationDelivery,
  EngagementPattern,
  TimingPreference,
  ContextualTrigger,
  SmartNotification,
  QuietHours,
  ChannelPreference,
  AINotificationType,
  AIError,
} from '@coffeeplexity/shared';
import type { RootState } from '../index';

interface NotificationIntelligenceSliceState {
  optimizationSettings: NotificationOptimization | null;
  deliveryHistory: NotificationDelivery[];
  engagementPatterns: EngagementPattern[];
  timingPreferences: TimingPreference[];
  contextualTriggers: ContextualTrigger[];
  activeNotifications: SmartNotification[];
  scheduledNotifications: SmartNotification[];
  loading: {
    optimization: boolean;
    delivery: boolean;
    patterns: boolean;
    timing: boolean;
    triggers: boolean;
    notifications: boolean;
  };
  error: AIError | null;
  lastUpdated: string | null;
  cacheExpiry: string | null;
  notificationPermission: 'granted' | 'denied' | 'default';
  quietModeActive: boolean;
}

const initialState: NotificationIntelligenceSliceState = {
  optimizationSettings: null,
  deliveryHistory: [],
  engagementPatterns: [],
  timingPreferences: [],
  contextualTriggers: [],
  activeNotifications: [],
  scheduledNotifications: [],
  loading: {
    optimization: false,
    delivery: false,
    patterns: false,
    timing: false,
    triggers: false,
    notifications: false,
  },
  error: null,
  lastUpdated: null,
  cacheExpiry: null,
  notificationPermission: 'default',
  quietModeActive: false,
};

// Async thunks
export const fetchNotificationOptimization = createAsyncThunk(
  'notificationIntelligence/fetchOptimization',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(
        `/api/ai/notifications/optimization/${userId}`
      );
      if (!response.ok) {
        throw new Error(
          `Failed to fetch notification optimization: ${response.statusText}`
        );
      }
      const optimization: NotificationOptimization = await response.json();
      return optimization;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'NOTIFICATION_OPTIMIZATION_ERROR',
        message: error.message || 'Failed to fetch notification optimization',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const updateNotificationOptimization = createAsyncThunk(
  'notificationIntelligence/updateOptimization',
  async (
    optimization: Partial<NotificationOptimization>,
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/notifications/optimization', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(optimization),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to update notification optimization: ${response.statusText}`
        );
      }
      const updatedOptimization: NotificationOptimization =
        await response.json();
      return updatedOptimization;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'NOTIFICATION_UPDATE_ERROR',
        message: error.message || 'Failed to update notification optimization',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchEngagementPatterns = createAsyncThunk(
  'notificationIntelligence/fetchEngagementPatterns',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/notifications/patterns/${userId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch engagement patterns: ${response.statusText}`
        );
      }
      const patterns: EngagementPattern[] = await response.json();
      return patterns;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ENGAGEMENT_PATTERNS_ERROR',
        message: error.message || 'Failed to fetch engagement patterns',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchTimingPreferences = createAsyncThunk(
  'notificationIntelligence/fetchTimingPreferences',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/notifications/timing/${userId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch timing preferences: ${response.statusText}`
        );
      }
      const preferences: TimingPreference[] = await response.json();
      return preferences;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'TIMING_PREFERENCES_ERROR',
        message: error.message || 'Failed to fetch timing preferences',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const fetchContextualTriggers = createAsyncThunk(
  'notificationIntelligence/fetchContextualTriggers',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/notifications/triggers/${userId}`);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch contextual triggers: ${response.statusText}`
        );
      }
      const triggers: ContextualTrigger[] = await response.json();
      return triggers;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'CONTEXTUAL_TRIGGERS_ERROR',
        message: error.message || 'Failed to fetch contextual triggers',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const scheduleSmartNotification = createAsyncThunk(
  'notificationIntelligence/scheduleNotification',
  async (
    notification: Omit<SmartNotification, 'id' | 'scheduledFor'>,
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/notifications/schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(notification),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to schedule smart notification: ${response.statusText}`
        );
      }
      const scheduledNotification: SmartNotification = await response.json();
      return scheduledNotification;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'NOTIFICATION_SCHEDULE_ERROR',
        message: error.message || 'Failed to schedule smart notification',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const recordNotificationEngagement = createAsyncThunk(
  'notificationIntelligence/recordEngagement',
  async (
    {
      notificationId,
      engagementType,
      context,
    }: {
      notificationId: string;
      engagementType: 'opened' | 'clicked' | 'dismissed';
      context?: Record<string, any>;
    },
    { rejectWithValue }
  ) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch('/api/ai/notifications/engagement', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notificationId,
          engagementType,
          context,
          timestamp: new Date().toISOString(),
        }),
      });
      if (!response.ok) {
        throw new Error(
          `Failed to record notification engagement: ${response.statusText}`
        );
      }
      const delivery: NotificationDelivery = await response.json();
      return delivery;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'ENGAGEMENT_RECORD_ERROR',
        message: error.message || 'Failed to record notification engagement',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

export const optimizeNotificationTiming = createAsyncThunk(
  'notificationIntelligence/optimizeTiming',
  async (userId: string, { rejectWithValue }) => {
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/ai/notifications/optimize/${userId}`, {
        method: 'POST',
      });
      if (!response.ok) {
        throw new Error(
          `Failed to optimize notification timing: ${response.statusText}`
        );
      }
      const optimization: NotificationOptimization = await response.json();
      return optimization;
    } catch (error: any) {
      const aiError: AIError = {
        code: 'TIMING_OPTIMIZATION_ERROR',
        message: error.message || 'Failed to optimize notification timing',
        recoverable: true,
        fallbackStrategy: 'rule_based',
      };
      return rejectWithValue(aiError);
    }
  }
);

const notificationIntelligenceSlice = createSlice({
  name: 'notificationIntelligence',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearNotificationData: state => {
      state.optimizationSettings = null;
      state.deliveryHistory = [];
      state.engagementPatterns = [];
      state.timingPreferences = [];
      state.contextualTriggers = [];
      state.activeNotifications = [];
      state.scheduledNotifications = [];
      state.lastUpdated = null;
      state.cacheExpiry = null;
    },
    setNotificationPermission: (
      state,
      action: PayloadAction<'granted' | 'denied' | 'default'>
    ) => {
      state.notificationPermission = action.payload;
    },
    setQuietModeActive: (state, action: PayloadAction<boolean>) => {
      state.quietModeActive = action.payload;
    },
    addActiveNotification: (
      state,
      action: PayloadAction<SmartNotification>
    ) => {
      state.activeNotifications.unshift(action.payload);
      // Keep only the most recent 20 active notifications
      if (state.activeNotifications.length > 20) {
        state.activeNotifications = state.activeNotifications.slice(0, 20);
      }
    },
    removeActiveNotification: (state, action: PayloadAction<string>) => {
      state.activeNotifications = state.activeNotifications.filter(
        notification => notification.id !== action.payload
      );
    },
    updateScheduledNotification: (
      state,
      action: PayloadAction<SmartNotification>
    ) => {
      const existingIndex = state.scheduledNotifications.findIndex(
        notification => notification.id === action.payload.id
      );
      if (existingIndex >= 0) {
        state.scheduledNotifications[existingIndex] = action.payload;
      } else {
        state.scheduledNotifications.push(action.payload);
      }
    },
    removeScheduledNotification: (state, action: PayloadAction<string>) => {
      state.scheduledNotifications = state.scheduledNotifications.filter(
        notification => notification.id !== action.payload
      );
    },
    updateQuietHours: (state, action: PayloadAction<Partial<QuietHours>>) => {
      if (state.optimizationSettings) {
        state.optimizationSettings.quietHours = {
          ...state.optimizationSettings.quietHours,
          ...action.payload,
        };
      }
    },
    updateChannelPreference: (
      state,
      action: PayloadAction<ChannelPreference>
    ) => {
      if (state.optimizationSettings) {
        const existingIndex =
          state.optimizationSettings.channelPreferences.findIndex(
            pref => pref.channel === action.payload.channel
          );
        if (existingIndex >= 0) {
          state.optimizationSettings.channelPreferences[existingIndex] =
            action.payload;
        } else {
          state.optimizationSettings.channelPreferences.push(action.payload);
        }
      }
    },
    updateContextualTrigger: (
      state,
      action: PayloadAction<ContextualTrigger>
    ) => {
      const existingIndex = state.contextualTriggers.findIndex(
        trigger => trigger.id === action.payload.id
      );
      if (existingIndex >= 0) {
        state.contextualTriggers[existingIndex] = action.payload;
      } else {
        state.contextualTriggers.push(action.payload);
      }
    },
    optimisticUpdateOptimization: (
      state,
      action: PayloadAction<Partial<NotificationOptimization>>
    ) => {
      if (state.optimizationSettings) {
        state.optimizationSettings = {
          ...state.optimizationSettings,
          ...action.payload,
        };
      }
    },
    setCacheExpiry: (state, action: PayloadAction<string>) => {
      state.cacheExpiry = action.payload;
    },
  },
  extraReducers: builder => {
    // Fetch Notification Optimization
    builder
      .addCase(fetchNotificationOptimization.pending, state => {
        state.loading.optimization = true;
        state.error = null;
      })
      .addCase(fetchNotificationOptimization.fulfilled, (state, action) => {
        state.loading.optimization = false;
        state.optimizationSettings = action.payload;
        state.lastUpdated = new Date().toISOString();
        state.cacheExpiry = new Date(Date.now() + 60 * 60 * 1000).toISOString(); // 1 hour
      })
      .addCase(fetchNotificationOptimization.rejected, (state, action) => {
        state.loading.optimization = false;
        state.error = action.payload as AIError;
      });

    // Update Notification Optimization
    builder
      .addCase(updateNotificationOptimization.pending, state => {
        state.loading.optimization = true;
        state.error = null;
      })
      .addCase(updateNotificationOptimization.fulfilled, (state, action) => {
        state.loading.optimization = false;
        state.optimizationSettings = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(updateNotificationOptimization.rejected, (state, action) => {
        state.loading.optimization = false;
        state.error = action.payload as AIError;
      });

    // Fetch Engagement Patterns
    builder
      .addCase(fetchEngagementPatterns.pending, state => {
        state.loading.patterns = true;
        state.error = null;
      })
      .addCase(fetchEngagementPatterns.fulfilled, (state, action) => {
        state.loading.patterns = false;
        state.engagementPatterns = action.payload;
      })
      .addCase(fetchEngagementPatterns.rejected, (state, action) => {
        state.loading.patterns = false;
        state.error = action.payload as AIError;
      });

    // Fetch Timing Preferences
    builder
      .addCase(fetchTimingPreferences.pending, state => {
        state.loading.timing = true;
        state.error = null;
      })
      .addCase(fetchTimingPreferences.fulfilled, (state, action) => {
        state.loading.timing = false;
        state.timingPreferences = action.payload;
      })
      .addCase(fetchTimingPreferences.rejected, (state, action) => {
        state.loading.timing = false;
        state.error = action.payload as AIError;
      });

    // Fetch Contextual Triggers
    builder
      .addCase(fetchContextualTriggers.pending, state => {
        state.loading.triggers = true;
        state.error = null;
      })
      .addCase(fetchContextualTriggers.fulfilled, (state, action) => {
        state.loading.triggers = false;
        state.contextualTriggers = action.payload;
      })
      .addCase(fetchContextualTriggers.rejected, (state, action) => {
        state.loading.triggers = false;
        state.error = action.payload as AIError;
      });

    // Schedule Smart Notification
    builder
      .addCase(scheduleSmartNotification.pending, state => {
        state.loading.notifications = true;
        state.error = null;
      })
      .addCase(scheduleSmartNotification.fulfilled, (state, action) => {
        state.loading.notifications = false;
        state.scheduledNotifications.push(action.payload);
      })
      .addCase(scheduleSmartNotification.rejected, (state, action) => {
        state.loading.notifications = false;
        state.error = action.payload as AIError;
      });

    // Record Notification Engagement
    builder
      .addCase(recordNotificationEngagement.pending, state => {
        state.loading.delivery = true;
      })
      .addCase(recordNotificationEngagement.fulfilled, (state, action) => {
        state.loading.delivery = false;
        state.deliveryHistory.unshift(action.payload);
        // Keep only the most recent 200 delivery records
        if (state.deliveryHistory.length > 200) {
          state.deliveryHistory = state.deliveryHistory.slice(0, 200);
        }
      })
      .addCase(recordNotificationEngagement.rejected, (state, action) => {
        state.loading.delivery = false;
        state.error = action.payload as AIError;
      });

    // Optimize Notification Timing
    builder
      .addCase(optimizeNotificationTiming.pending, state => {
        state.loading.optimization = true;
      })
      .addCase(optimizeNotificationTiming.fulfilled, (state, action) => {
        state.loading.optimization = false;
        state.optimizationSettings = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(optimizeNotificationTiming.rejected, (state, action) => {
        state.loading.optimization = false;
        state.error = action.payload as AIError;
      });
  },
});

export const {
  clearError,
  clearNotificationData,
  setNotificationPermission,
  setQuietModeActive,
  addActiveNotification,
  removeActiveNotification,
  updateScheduledNotification,
  removeScheduledNotification,
  updateQuietHours,
  updateChannelPreference,
  updateContextualTrigger,
  optimisticUpdateOptimization,
  setCacheExpiry,
} = notificationIntelligenceSlice.actions;

// Basic selectors
export const selectNotificationOptimization = (state: RootState) =>
  state.notificationIntelligence.optimizationSettings;
export const selectDeliveryHistory = (state: RootState) =>
  state.notificationIntelligence.deliveryHistory;
export const selectEngagementPatterns = (state: RootState) =>
  state.notificationIntelligence.engagementPatterns;
export const selectTimingPreferences = (state: RootState) =>
  state.notificationIntelligence.timingPreferences;
export const selectContextualTriggers = (state: RootState) =>
  state.notificationIntelligence.contextualTriggers;
export const selectActiveNotifications = (state: RootState) =>
  state.notificationIntelligence.activeNotifications;
export const selectScheduledNotifications = (state: RootState) =>
  state.notificationIntelligence.scheduledNotifications;
export const selectNotificationLoading = (state: RootState) =>
  state.notificationIntelligence.loading;
export const selectNotificationError = (state: RootState) =>
  state.notificationIntelligence.error;
export const selectNotificationPermission = (state: RootState) =>
  state.notificationIntelligence.notificationPermission;
export const selectQuietModeActive = (state: RootState) =>
  state.notificationIntelligence.quietModeActive;

// Memoized selectors
export const selectOptimalNotificationTimes = createSelector(
  [selectNotificationOptimization],
  optimization => {
    return optimization?.optimalTimes || [];
  }
);

export const selectQuietHours = createSelector(
  [selectNotificationOptimization],
  optimization => {
    return (
      optimization?.quietHours || {
        enabled: false,
        startTime: '22:00',
        endTime: '08:00',
        timezone: 'UTC',
      }
    );
  }
);

export const selectChannelPreferences = createSelector(
  [selectNotificationOptimization],
  optimization => {
    return optimization?.channelPreferences || [];
  }
);

export const selectEnabledChannels = createSelector(
  [selectChannelPreferences],
  channelPreferences => {
    return channelPreferences.filter(pref => pref.enabled);
  }
);

export const selectRecentEngagement = createSelector(
  [selectDeliveryHistory],
  deliveryHistory => {
    const oneWeekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return deliveryHistory.filter(
      delivery => new Date(delivery.deliveredAt) > oneWeekAgo
    );
  }
);

export const selectEngagementRate = createSelector(
  [selectRecentEngagement],
  recentEngagement => {
    if (recentEngagement.length === 0) return 0;
    const engagedCount = recentEngagement.filter(
      delivery => delivery.openedAt || delivery.clickedAt
    ).length;
    return engagedCount / recentEngagement.length;
  }
);

export const selectActiveTriggersCount = createSelector(
  [selectContextualTriggers],
  triggers => {
    return triggers.filter(trigger => trigger.enabled).length;
  }
);

export const selectHighPriorityNotifications = createSelector(
  [selectActiveNotifications],
  notifications => {
    return notifications.filter(
      notification => notification.priority === 'high'
    );
  }
);

export const selectNotificationsByType = createSelector(
  [selectActiveNotifications],
  notifications => {
    return notifications.reduce(
      (acc, notification) => {
        if (!acc[notification.type]) {
          acc[notification.type] = [];
        }
        acc[notification.type].push(notification);
        return acc;
      },
      {} as Record<AINotificationType, SmartNotification[]>
    );
  }
);

export const selectUpcomingScheduledNotifications = createSelector(
  [selectScheduledNotifications],
  scheduledNotifications => {
    const now = new Date();
    const next24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);
    return scheduledNotifications
      .filter(notification => {
        const scheduledTime = new Date(notification.scheduledFor);
        return scheduledTime > now && scheduledTime <= next24Hours;
      })
      .sort(
        (a, b) =>
          new Date(a.scheduledFor).getTime() -
          new Date(b.scheduledFor).getTime()
      );
  }
);

export const selectBestEngagementTimes = createSelector(
  [selectTimingPreferences],
  timingPreferences => {
    return timingPreferences
      .filter(pref => pref.engagementRate > 0.3 && pref.sampleSize >= 5)
      .sort((a, b) => b.engagementRate - a.engagementRate)
      .slice(0, 5);
  }
);

export const selectEngagementPatternsByType = createSelector(
  [selectEngagementPatterns],
  patterns => {
    return patterns.reduce(
      (acc, pattern) => {
        if (!acc[pattern.patternType]) {
          acc[pattern.patternType] = [];
        }
        if (!acc[pattern.patternType]) {
          acc[pattern.patternType] = [];
        }
        acc[pattern.patternType].push(pattern);
        return acc;
      },
      {} as Record<string, EngagementPattern[]>
    );
  }
);

export const selectIsInQuietHours = createSelector(
  [selectQuietHours],
  quietHours => {
    if (!quietHours.enabled) return false;

    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5); // HH:MM format

    // Simple time comparison (doesn't handle timezone properly, but good for demo)
    return (
      currentTime >= quietHours.startTime || currentTime <= quietHours.endTime
    );
  }
);

export const selectNotificationCacheStale = createSelector(
  [
    selectNotificationOptimization,
    (state: RootState) => state.notificationIntelligence.cacheExpiry,
  ],
  (optimization, cacheExpiry) => {
    if (!optimization || !cacheExpiry) return true;
    return new Date() > new Date(cacheExpiry);
  }
);

export default notificationIntelligenceSlice.reducer;
